/**
 * The words for a stored condition's operator, for a surface that shows a condition as text rather than in
 * the editor — a filter chip, say. Keyed by the canonical AST operator (`==`, `notIn`, …: what a
 * `ConditionDto` stores), so a reader of a stored filter needs nothing from the query builder.
 *
 * The list, null and text operators reuse the editor's own labels (`raqb.op.*`), so a chip and the dropdown
 * the condition was built in say the same thing. The comparisons, which the editor shows as their symbols,
 * have words of their own beside them: a chip reads as a sentence, and `Account number != 4010` is not one.
 *
 * Free of react-awesome-query-builder on purpose: the editor is lazy-loaded because that library is large,
 * and naming a chip's operator must not pull it into the page's own chunk.
 */
const OPERATOR_WORD_KEYS: Readonly<Record<string, string>> = {
  '==': 'raqb.op.equal',
  '!=': 'raqb.op.notEqual',
  '>': 'raqb.op.greater',
  '>=': 'raqb.op.greaterOrEqual',
  '<': 'raqb.op.less',
  '<=': 'raqb.op.lessOrEqual',
  contains: 'raqb.op.contains',
  startsWith: 'raqb.op.startsWith',
  in: 'raqb.op.anyOf',
  notIn: 'raqb.op.noneOf',
  isNull: 'raqb.op.isNull',
  isNotNull: 'raqb.op.isNotNull',
};

/**
 * The operator as a word in the reader's language, lower-cased to sit inside a sentence. An operator this
 * app has no word for is shown as stored rather than hidden — a chip that drops it would claim a different
 * condition than the one filtering the page.
 */
export function operatorWord(translate: (key: string) => string, operator: string, language: string): string {
  const key = OPERATOR_WORD_KEYS[operator];
  return key ? translate(key).toLocaleLowerCase(language) : operator;
}
